import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ClassCard } from '../components/classes/EventCard';
import { ClassFilter } from '../components/classes/EventFilter';
import { useClasses } from '../hooks/useClasses';
import { useLocations } from '../hooks/useLocations';
import { useAuth } from '../contexts/AuthContext';

interface Filters {
  locationId: string;
  startDate: string;
  availableOnly: boolean;
}

export const ClassesPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { locations } = useLocations();
  const [filters, setFilters] = useState<Filters>({
    locationId: '',
    startDate: new Date().toISOString().split('T')[0],
    availableOnly: false,
  });

  useEffect(() => {
    const defaultId = currentUser?.defaultLocations[0];
    if (!defaultId) return;
    setFilters(prev => prev.locationId ? prev : { ...prev, locationId: defaultId });
  }, [currentUser]);

  const queryParams = useMemo(
    () => filters.locationId ? {
      locationId: filters.locationId,
      startDate: filters.startDate || undefined,
      availableOnly: filters.availableOnly || undefined,
    } : null,
    [filters.locationId, filters.startDate, filters.availableOnly]
  );

  const { classes, loading, error } = useClasses(queryParams);

  const canCreateClass = currentUser?.roles.includes('Teacher') ||
    currentUser?.roles.includes('Organizer') ||
    currentUser?.roles.includes('Admin');

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Upcoming Classes</h1>
        {canCreateClass && (
          <Link
            to="/classes/create"
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
          >
            Create Class
          </Link>
        )}
      </div>

      <div className="mb-6">
        <ClassFilter filters={filters} locations={locations} onChange={setFilters} />
      </div>

      {!filters.locationId && (
        <p className="text-center text-gray-500 py-12">Select a location to see classes.</p>
      )}

      {filters.locationId && loading && (
        <div className="text-center py-12 text-gray-500">Loading classes…</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm">{error}</div>
      )}

      {filters.locationId && !loading && !error && classes.length === 0 && (
        <p className="text-center text-gray-500 py-12">No classes found for the selected filters.</p>
      )}

      {classes.length > 0 && (
        <div className="space-y-4">
          {classes.map(cls => (
            <ClassCard key={cls.eventId} event={cls} />
          ))}
        </div>
      )}
    </div>
  );
};
